import React, { useState, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilter,
  faChartBar,
  faTimes,
  faPlus,
  faSync,
  faFileExport,
  faChartLine,
  faChartArea,
  faServer,
  faGlobe,
  faUsers,
  faTable,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { faAws, faGoogle, faGithub } from '@fortawesome/free-brands-svg-icons';
import {
  ResponsiveContainer,
  LineChart, Line,
  AreaChart, Area,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { CostData } from '../types';
import { costService } from '../services/costService';

interface ReportTabProps {
  data: CostData | null;
  loading?: boolean;
}

// SIR FEEDBACK: report tab should mirror AWS Cost Explorer — keep only fields
// that are actually wired to a working backend. Removed dimensions (instance_type,
// usage_type, resource, tag, cost_category) and dead toggles (showForecasted,
// showUntagged, showUncategorized) until the backend supports them.
// weekly/yearly granularity removed (Cost Explorer's GetCostAndUsage primarily
// supports DAILY/MONTHLY/HOURLY; we expose DAILY + MONTHLY).
//
// costType (Unblended / Blended / Amortized / NetAmortized / NetUnblended) is
// kept and now actually wired — backend uses it as the Cost Explorer `Metrics`
// parameter so the math actually changes.
interface ReportFilters {
  timeRange: {
    start: string;
    end: string;
    preset?: '7d' | '30d' | '90d' | '12m' | 'custom';
  };
  granularity: 'daily' | 'monthly';
  groupBy: {
    dimension: 'service' | 'account' | 'region';
  };
  costType: 'UnblendedCost' | 'BlendedCost' | 'AmortizedCost' | 'NetUnblendedCost' | 'NetAmortizedCost';
  services: string[];
  accounts: string[];
  regions: string[];
}

interface ReportData {
  chartData: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      backgroundColor?: string;
      borderColor?: string;
    }[];
  };
  tableData: {
    headers: string[];
    rows: (string | number)[][];
  };
  summary: {
    totalCost: number;
    averageCost: number;
    highestCost: number;
    lowestCost: number;
    trend: number;
  };
}

const defaultFilters: ReportFilters = {
  timeRange: {
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
    preset: '7d'
  },
  granularity: 'daily',
  groupBy: {
    dimension: 'service'
  },
  costType: 'UnblendedCost',
  services: [],
  accounts: [],
  regions: []
};

// SIR FEEDBACK: pie chart removed — AWS Cost Explorer doesn't use pie for
// time-series data (it shows totals/snapshots). Kept line/area/bar to match.
const chartTypes = [
  { id: 'line', name: 'Line Chart', icon: faChartLine },
  { id: 'area', name: 'Area Chart', icon: faChartArea },
  { id: 'bar', name: 'Bar Chart', icon: faChartBar }
];

const FilterSection: React.FC<{
  title: string;
  children: React.ReactNode;
  collapsed?: boolean;
  onToggle?: () => void;
}> = ({ title, children, collapsed = false, onToggle }) => (
  <div className="border border-gray-300 rounded mb-3">
    <button
      onClick={onToggle}
      className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
    >
      <span className="text-xs font-medium text-gray-900">{title}</span>
      <FontAwesomeIcon 
        icon={collapsed ? faPlus : faTimes} 
        className="text-gray-500 text-xs"
      />
    </button>
    {!collapsed && (
      <div className="p-4 border-t border-gray-300">
        {children}
      </div>
    )}
  </div>
);

// SIR FEEDBACK: Original generateReportDataFromActualData was FAKE — it took the
// total spend, divided by N points, and added Math.random() noise. AWS Cost Explorer
// returns real time-series via GetCostAndUsage. Replaced with a transform that converts
// the API response (labels + groups[]) into ReportData (chartData + tableData + summary).
//
// LEGACY (kept as reference, do not use):
//   const baseValue = actualData ? (actualData.summary.totalSpend / points) + (Math.random() * 500 - 250) : 1000;
const transformApiReportToData = (
  filters: ReportFilters,
  apiData: { labels: string[]; groups: Array<{ name: string; data: number[]; total: number }>; summary: { totalCost: number; averageCost: number; highestCost: number; lowestCost: number; trend: number } }
): ReportData => {
  const labels = apiData.labels || [];
  const groups = apiData.groups || [];

  const palette = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#8BC34A', '#E91E63'];
  const datasets = groups.slice(0, 8).map((g, i) => ({
    label: g.name,
    data: g.data,
    backgroundColor: palette[i % palette.length],
    borderColor: palette[i % palette.length]
  }));

  const showDatesInTable = filters.granularity === 'daily';
  const tableHeaders = showDatesInTable
    ? ['Date', filters.groupBy.dimension.toUpperCase(), 'Cost', 'Change']
    : [filters.groupBy.dimension.toUpperCase(), 'Total Cost', 'Percentage', 'Trend'];

  const tableRows: (string | number)[][] = [];
  if (showDatesInTable) {
    labels.forEach((label, idx) => {
      datasets.forEach(ds => {
        const cost = ds.data[idx] || 0;
        const prev = idx > 0 ? (ds.data[idx - 1] || 0) : cost;
        const change = prev > 0 ? ((cost - prev) / prev) * 100 : 0;
        tableRows.push([
          label,
          ds.label,
          `$${Math.round(cost).toLocaleString()}`,
          `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`
        ]);
      });
    });
  } else {
    const total = groups.reduce((s, g) => s + g.total, 0);
    datasets.forEach((ds, i) => {
      const grpTotal = groups[i]?.total || 0;
      const pct = total > 0 ? (grpTotal / total) * 100 : 0;
      const first = ds.data[0] || 0;
      const last = ds.data[ds.data.length - 1] || 0;
      const trend = first > 0 ? ((last - first) / first) * 100 : 0;
      tableRows.push([
        ds.label,
        `$${Math.round(grpTotal).toLocaleString()}`,
        `${pct.toFixed(1)}%`,
        `${trend >= 0 ? '+' : ''}${trend.toFixed(1)}%`
      ]);
    });
  }

  return {
    chartData: { labels, datasets },
    tableData: { headers: tableHeaders, rows: tableRows },
    summary: apiData.summary
  };
};

// SIR FEEDBACK: legacy `generateReportDataFromActualData` deleted. It was the
// fake generator that took the cloud total, divided by N points, and added
// Math.random() noise — never a real time series. Replaced by the API-driven
// path: costService.getCustomReport() → transformApiReportToData() above.
// (Original implementation is preserved in git history if you need to compare.)

export const ReportTab: React.FC<ReportTabProps> = ({ data, loading = false }) => {
  const [filters, setFilters] = useState<ReportFilters>(defaultFilters);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [chartType, setChartType] = useState('line');
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  // Region list — Cost Summary API doesn't return regions, so we fetch them once
  // via our own /analysis/report endpoint with groupBy=region (last 30 days).
  const [regionList, setRegionList] = useState<Array<{ region: string; totalCost: number }>>([]);
  // SIR FEEDBACK: 'advanced' section removed (forecasted/untagged/uncategorized were dead UI).
  // costType moved out of "Advanced" — it's now a real filter wired to the backend Metrics param.
  // Cloud Provider section kept simple — shows only providers actually connected.
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    time: false,
    grouping: false,
    costType: true,
    cloudProviders: true,
    services: true,
    accounts: true,
    regions: true
  });

  const toggleSection = useCallback((section: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  const updateFilter = useCallback((key: keyof ReportFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Reset filters to defaults — re-applies time range (Last 7 days), daily,
  // service group-by, Unblended cost, and re-ticks all services/accounts/regions.
  const clearAllFilters = useCallback(() => {
    setFilters({
      ...defaultFilters,
      services: (data?.clouds.aws.services || []).map(s => s.serviceName),
      accounts: (data?.clouds.aws.accounts || []).map(a => a.accountId),
      regions: regionList.map(r => r.region)
    });
  }, [data, regionList]);

  // Available items (used to drive pre-ticked checkboxes + decide when to send filter).
  const availableServices = (data?.clouds.aws.services || []).map(s => s.serviceName);
  const availableAccountIds = (data?.clouds.aws.accounts || []).map(a => a.accountId);
  const availableRegions = regionList.map(r => r.region);

  // Real Cost Explorer-driven report. Hits /api/cost/analysis/report which calls
  // AWS Cost Explorer's GetCostAndUsage with the user's filters.
  //
  // Filter semantics (Cost Explorer style):
  //   - all ticked       → "no filter" (don't send the param) → backend returns all
  //   - some ticked      → send subset → backend filters to those
  //   - none ticked      → "user excluded everything" → return empty result locally
  //                        (don't even hit backend — equivalent to AWS Console showing
  //                        empty when filter selects nothing)
  const generateReport = useCallback(async () => {
    setIsGenerating(true);
    setReportError(null);
    try {
      const granularity = filters.granularity === 'monthly' ? 'MONTHLY' : 'DAILY';

      const allServicesTicked = availableServices.length > 0 && filters.services.length === availableServices.length;
      const allAccountsTicked = availableAccountIds.length > 0 && filters.accounts.length === availableAccountIds.length;
      const allRegionsTicked = availableRegions.length > 0 && filters.regions.length === availableRegions.length;

      const noServicesPicked = availableServices.length > 0 && filters.services.length === 0;
      const noAccountsPicked = availableAccountIds.length > 0 && filters.accounts.length === 0;
      const noRegionsPicked = availableRegions.length > 0 && filters.regions.length === 0;

      if (noServicesPicked || noAccountsPicked || noRegionsPicked) {
        setReportData({
          chartData: { labels: [], datasets: [] },
          tableData: { headers: [], rows: [] },
          summary: { totalCost: 0, averageCost: 0, highestCost: 0, lowestCost: 0, trend: 0 }
        });
        return;
      }

      const apiData = await costService.getCustomReport({
        startDate: filters.timeRange.start,
        endDate: filters.timeRange.end,
        granularity,
        groupBy: filters.groupBy.dimension,
        costType: filters.costType,
        services: allServicesTicked ? [] : filters.services,
        accounts: allAccountsTicked ? [] : filters.accounts,
        regions: allRegionsTicked ? [] : filters.regions
      });
      setReportData(transformApiReportToData(filters, apiData));
    } catch (err: any) {
      console.error('Report generation failed:', err);
      setReportError(err?.message || 'Failed to generate report');
      setReportData(null);
    } finally {
      setIsGenerating(false);
    }
  }, [filters]);

  // Auto-generate when filters change. data prop kept for filter dropdowns (services/accounts list).
  useEffect(() => {
    generateReport();
  }, [generateReport]);

  // Pre-tick all services when the cost data loads. User unticks specific items to filter.
  // (Cost Explorer-style — default view shows everything, user narrows down.)
  useEffect(() => {
    const all = (data?.clouds.aws.services || []).map(s => s.serviceName);
    if (all.length === 0) return;
    setFilters(prev => prev.services.length === 0 ? { ...prev, services: all } : prev);
  }, [data]);

  // Pre-tick all accounts when data loads.
  useEffect(() => {
    const all = (data?.clouds.aws.accounts || []).map(a => a.accountId);
    if (all.length === 0) return;
    setFilters(prev => prev.accounts.length === 0 ? { ...prev, accounts: all } : prev);
  }, [data]);

  // Pre-tick all regions whenever the region list refreshes.
  useEffect(() => {
    if (regionList.length === 0) return;
    const all = regionList.map(r => r.region);
    setFilters(prev => prev.regions.length === 0 ? { ...prev, regions: all } : prev);
  }, [regionList]);

  // Load region list using the SAME time range as the report. Re-fetches when time
  // range changes so amounts shown next to each region stay consistent with the
  // summary cards / chart (mirrors AWS Cost Explorer's filter-list behavior).
  useEffect(() => {
    let cancelled = false;
    costService.getCustomReport({
      startDate: filters.timeRange.start,
      endDate: filters.timeRange.end,
      granularity: filters.granularity === 'monthly' ? 'MONTHLY' : 'DAILY',
      groupBy: 'region'
    }).then(res => {
      if (cancelled) return;
      const regions = (res.groups || [])
        .filter(g => g.total > 0 && g.name && g.name !== 'NoRegion')
        .map(g => ({ region: g.name, totalCost: g.total }));
      setRegionList(regions);
    }).catch(err => {
      console.warn('Failed to load region list:', err.message);
    });
    return () => { cancelled = true; };
  }, [filters.timeRange.start, filters.timeRange.end, filters.granularity]);

  const exportToCSV = useCallback(() => {
    if (!reportData) return;
    
    // Add header with filter information
    const filterInfo = [
      `Cost Report - ${filters.groupBy.dimension.toUpperCase()}`,
      `Date Range: ${filters.timeRange.start} to ${filters.timeRange.end}`,
      `Granularity: ${filters.granularity}`,
      `Cost Type: ${filters.costType}`,
      `Cloud Provider: AWS`,
      `Generated: ${new Date().toLocaleString()}`,
      '', // Empty row
      reportData.tableData.headers.join(',')
    ];
    
    const csvContent = [
      ...filterInfo,
      ...reportData.tableData.rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    // Include date range in filename
    const startDate = filters.timeRange.start.replace(/-/g, '');
    const endDate = filters.timeRange.end.replace(/-/g, '');
    const groupBy = filters.groupBy.dimension;
    a.download = `cost-report-${groupBy}-${startDate}-${endDate}.csv`;
    
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, [reportData, filters]);

  const presetTimeRanges = [
    { id: '7d', label: 'Last 7 days', days: 7 },
    { id: '30d', label: 'Last 30 days', days: 30 },
    { id: '90d', label: 'Last 90 days', days: 90 },
    { id: '12m', label: 'Last 12 months', days: 365 }
  ];

  const setPresetTimeRange = (preset: string, days: number) => {
    const end = new Date();
    const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000);
    updateFilter('timeRange', {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
      preset
    });
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded p-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xs font-semibold text-gray-900 mb-1 flex items-center">
              <FontAwesomeIcon icon={faChartBar} className="text-indigo-600 mr-2" />
              Multi-Cloud Cost Reports
            </h2>
            <p className="text-xs text-gray-600">
              Advanced cost analysis and reporting across AWS, GCP, GitHub and custom data sources
              <br />
              <span className="text-xs text-indigo-600">
                {isGenerating ? '⚡ Updating report...' : '✨ Report updates automatically as you change filters'}
              </span>
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {reportData && (
              <button
                onClick={exportToCSV}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-xs font-medium"
              >
                <FontAwesomeIcon icon={faFileExport} className="mr-2" />
                Export CSV
              </button>
            )}
            <button
              onClick={generateReport}
              disabled={isGenerating}
              className="px-3 py-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50 transition-colors text-xs font-medium"
            >
              <FontAwesomeIcon icon={faSync} className={`mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
              {isGenerating ? 'Updating...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        {/* Filters Panel */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-gray-300 rounded p-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-semibold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faFilter} className="text-gray-600 mr-2" />
                Report Parameters
              </h3>
              <button
                type="button"
                onClick={clearAllFilters}
                className="text-xs text-indigo-600 hover:text-indigo-800"
              >
                Clear All
              </button>
            </div>

            {/* Time Filters */}
            <FilterSection
              title="Time Range & Granularity"
              collapsed={collapsedSections.time}
              onToggle={() => toggleSection('time')}
            >
              <div className="space-y-4">
                {/* Preset Time Ranges */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Quick Select</label>
                  <div className="grid grid-cols-2 gap-2">
                    {presetTimeRanges.map(preset => (
                      <button
                        key={preset.id}
                        onClick={() => setPresetTimeRange(preset.id, preset.days)}
                        className={`px-3 py-2 text-xs rounded border transition-colors ${
                          filters.timeRange.preset === preset.id
                            ? 'bg-indigo-100 border-indigo-200 text-indigo-700'
                            : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Date Range */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Custom Date Range</label>
                  <div className="space-y-2">
                    <input
                      type="date"
                      value={filters.timeRange.start}
                      onChange={(e) => updateFilter('timeRange', { ...filters.timeRange, start: e.target.value, preset: 'custom' })}
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <input
                      type="date"
                      value={filters.timeRange.end}
                      onChange={(e) => updateFilter('timeRange', { ...filters.timeRange, end: e.target.value, preset: 'custom' })}
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Granularity */}
                {/* SIR FEEDBACK: weekly/yearly removed — AWS Cost Explorer's GetCostAndUsage
                    primarily supports DAILY and MONTHLY (HOURLY is paid-tier only). */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Granularity</label>
                  <select
                    value={filters.granularity}
                    onChange={(e) => updateFilter('granularity', e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="daily">Daily</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>
            </FilterSection>

            {/* Group By */}
            <FilterSection
              title="Group By & Dimensions"
              collapsed={collapsedSections.grouping}
              onToggle={() => toggleSection('grouping')}
            >
              <div className="space-y-4">
                {/* SIR FEEDBACK: group-by dimensions reduced to the three our backend wires
                    today — Service, Account, Region. instance_type / usage_type / resource /
                    tag / cost_category are valid Cost Explorer dimensions and will be added
                    once their backend mapping is in place. */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Group By Dimension</label>
                  <select
                    value={filters.groupBy.dimension}
                    onChange={(e) => updateFilter('groupBy', { ...filters.groupBy, dimension: e.target.value })}
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="service">Service</option>
                    <option value="account">Account</option>
                    <option value="region">Region</option>
                  </select>
                </div>
              </div>
            </FilterSection>

            {/* Cloud Provider — shows only providers actually connected (cost > 0).
                AWS is the only one wired to the report backend today; GCP/GitHub
                appear here when connected but stay disabled until their backends land. */}
            <FilterSection
              title="Cloud Provider"
              collapsed={collapsedSections.cloudProviders}
              onToggle={() => toggleSection('cloudProviders')}
            >
              <div className="space-y-2">
                {[
                  { id: 'aws', label: 'AWS', icon: faAws, color: 'text-orange-600', spend: data?.clouds.aws.totalSpend || 0, supported: true },
                  { id: 'gcp', label: 'Google Cloud', icon: faGoogle, color: 'text-blue-600', spend: data?.clouds.gcp.totalSpend || 0, supported: false },
                  { id: 'github', label: 'GitHub', icon: faGithub, color: 'text-gray-700', spend: data?.clouds.github.totalSpend || 0, supported: false }
                ]
                  .filter(p => p.spend > 0 || p.supported)
                  .map(p => (
                    <label
                      key={p.id}
                      className={`flex items-center text-xs ${p.supported ? 'text-gray-700' : 'text-gray-400 cursor-not-allowed'}`}
                    >
                      <input
                        type="checkbox"
                        checked={p.supported}
                        disabled={!p.supported}
                        readOnly
                        className="mr-2"
                      />
                      <FontAwesomeIcon icon={p.icon} className={`${p.color} mr-2`} />
                      {p.label}
                    </label>
                  ))}
              </div>
            </FilterSection>

            {/* Cost Type (AWS Cost Explorer "Cost Type" — Blended / Unblended / Amortized) */}
            <FilterSection
              title="Cost Type"
              collapsed={collapsedSections.costType}
              onToggle={() => toggleSection('costType')}
            >
              <div>
                <select
                  value={filters.costType}
                  onChange={(e) => updateFilter('costType', e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="UnblendedCost">Unblended</option>
                  <option value="BlendedCost">Blended</option>
                  <option value="AmortizedCost">Amortized</option>
                  <option value="NetUnblendedCost">Net Unblended</option>
                  <option value="NetAmortizedCost">Net Amortized</option>
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  How AWS calculates cost (RIs / Savings Plans amortization etc.)
                </p>
              </div>
            </FilterSection>

            {/* Services — pre-ticked by default (Cost Explorer style). User unticks to exclude. */}
            <FilterSection
              title="Services"
              collapsed={collapsedSections.services}
              onToggle={() => toggleSection('services')}
            >
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {data && (data.clouds.aws.services || []).length > 0 ? (
                  (data.clouds.aws.services || []).map(service => (
                    <label key={service.serviceName} className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        className="mr-2"
                        onChange={(e) => {
                          const newServices = e.target.checked
                            ? [...filters.services, service.serviceName]
                            : filters.services.filter(s => s !== service.serviceName);
                          updateFilter('services', newServices);
                        }}
                        checked={filters.services.includes(service.serviceName)}
                      />
                      <FontAwesomeIcon icon={faServer} className="text-gray-500 mr-2" />
                      <span className="truncate">{service.serviceName} (${service.totalCost.toLocaleString()})</span>
                    </label>
                  ))
                ) : (
                  <p className="text-[11px] text-gray-500 italic">No AWS services with cost data yet.</p>
                )}
              </div>
            </FilterSection>

            {/* Accounts */}
            <FilterSection
              title="Accounts"
              collapsed={collapsedSections.accounts}
              onToggle={() => toggleSection('accounts')}
            >
              {/* SIR FEEDBACK: GCP projects merged-as-accounts removed (Cost Explorer scope = AWS).
                  Hardcoded "dev-berlin (032760533965)" mock fallback removed too.
                  BUG FIX: send accountId (12-digit) to backend, not accountName — AWS Cost
                  Explorer's LINKED_ACCOUNT dimension matches by account ID, not display name.
                  Pre-ticked by default — untick to exclude (Cost Explorer style). */}
              <div className="space-y-2">
                {data && (data.clouds.aws.accounts || []).length > 0 ? (
                  (data.clouds.aws.accounts || []).map(account => (
                    <label key={account.accountId} className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        className="mr-2"
                        onChange={(e) => {
                          const newAccounts = e.target.checked
                            ? [...filters.accounts, account.accountId]
                            : filters.accounts.filter(a => a !== account.accountId);
                          updateFilter('accounts', newAccounts);
                        }}
                        checked={filters.accounts.includes(account.accountId)}
                      />
                      <FontAwesomeIcon icon={faUsers} className="text-gray-500 mr-2" />
                      {account.accountName} (${account.totalCost.toLocaleString()})
                    </label>
                  ))
                ) : (
                  <p className="text-[11px] text-gray-500 italic">No AWS accounts connected.</p>
                )}
              </div>
            </FilterSection>

            {/* Regions */}
            <FilterSection
              title="Regions"
              collapsed={collapsedSections.regions}
              onToggle={() => toggleSection('regions')}
            >
              {/* SIR FEEDBACK: GCP regions removed (Cost Explorer scope = AWS).
                  Hardcoded ['us-east-1', 'eu-west-1'] mock fallback removed.
                  Cost Summary API doesn't return regions — we fetch them separately
                  via /analysis/report?groupBy=region (see useEffect above).
                  Pre-ticked by default — untick to exclude. Scrollable if many regions. */}
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {regionList.length > 0 ? (
                  regionList.map(region => (
                    <label key={region.region} className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        className="mr-2"
                        onChange={(e) => {
                          const newRegions = e.target.checked
                            ? [...filters.regions, region.region]
                            : filters.regions.filter(r => r !== region.region);
                          updateFilter('regions', newRegions);
                        }}
                        checked={filters.regions.includes(region.region)}
                      />
                      <FontAwesomeIcon icon={faGlobe} className="text-gray-500 mr-2" />
                      {region.region} (${region.totalCost.toLocaleString(undefined, { maximumFractionDigits: 2 })})
                    </label>
                  ))
                ) : (
                  <p className="text-[11px] text-gray-500 italic">Loading regions…</p>
                )}
              </div>
            </FilterSection>

            {/*
              SIR FEEDBACK: entire "Advanced Options" panel removed.
              Reason: every control here was dead UI / not wired to a working backend:
                - Aggregate By (Blended / Unblended / Net Unblended) — currently selected
                  value is sent nowhere; Cost Explorer's `Metrics` parameter would need
                  to flip between UnblendedCost / BlendedCost / NetUnblendedCost.
                - Show forecasted values — no Cost Explorer GetCostForecast call.
                - Show only untagged resources — no filter logic.
                - Show only uncategorized resources — no filter logic.
              When backend gets the right wiring, this block can be re-enabled — kept
              the original JSX commented for reference rather than deleting it.

            <FilterSection
              title="Advanced Options"
              collapsed={collapsedSections.advanced}
              onToggle={() => toggleSection('advanced')}
            >
              ... (see git history) ...
            </FilterSection>
            */}
          </div>
        </div>

        {/* Report Content */}
        <div className="lg:col-span-3 space-y-3">
          {reportError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-xs">
              {reportError}
            </div>
          )}
          {isGenerating ? (
            <div className="bg-white border border-gray-300 rounded p-8 text-center">
              <FontAwesomeIcon icon={faSync} className="text-4xl text-indigo-600 mb-4 animate-spin" />
              <h4 className="text-xs font-medium text-gray-900 mb-2">Generating Report</h4>
              <p className="text-xs text-gray-600">
                Analyzing cost data across selected filters and time range...
              </p>
            </div>
          ) : reportData ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-300 rounded p-3">
                  <div className="text-xs text-gray-600 mb-1">Total Cost</div>
                  <div className="text-xs font-semibold text-gray-900">
                    ${reportData.summary.totalCost.toLocaleString()}
                  </div>
                </div>
                <div className="bg-white border border-gray-300 rounded p-3">
                  <div className="text-xs text-gray-600 mb-1">Average Cost</div>
                  <div className="text-xs font-semibold text-gray-900">
                    ${reportData.summary.averageCost.toLocaleString()}
                  </div>
                </div>
                <div className="bg-white border border-gray-300 rounded p-3">
                  <div className="text-xs text-gray-600 mb-1">Highest Cost</div>
                  <div className="text-xs font-semibold text-gray-900">
                    ${reportData.summary.highestCost.toLocaleString()}
                  </div>
                </div>
                <div className="bg-white border border-gray-300 rounded p-3">
                  <div className="text-xs text-gray-600 mb-1">Trend</div>
                  <div className={`text-sm font-semibold ${reportData.summary.trend >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {reportData.summary.trend > 0 ? '+' : ''}{reportData.summary.trend.toFixed(1)}%
                  </div>
                </div>
              </div>

              {/* Chart Section */}
              <div className="bg-white border border-gray-300 rounded p-3">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xs font-semibold text-gray-900">Cost Visualization</h4>
                  <div className="flex items-center space-x-2">
                    {chartTypes.map(type => (
                      <button
                        key={type.id}
                        onClick={() => setChartType(type.id)}
                        className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                          chartType === type.id
                            ? 'bg-indigo-100 border-indigo-200 text-indigo-700'
                            : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <FontAwesomeIcon icon={type.icon} className="mr-1" />
                        {type.name}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Real chart powered by recharts. Builds row-per-period objects from
                    chartData.labels + datasets so Line/Area/Bar can all share the same shape. */}
                {(() => {
                  const rechartsData = reportData.chartData.labels.map((label, i) => {
                    const row: Record<string, string | number> = { label };
                    reportData.chartData.datasets.forEach(ds => {
                      row[ds.label] = Number((ds.data[i] || 0).toFixed(2));
                    });
                    return row;
                  });

                  if (rechartsData.length === 0 || reportData.chartData.datasets.length === 0) {
                    return (
                      <div className="h-80 bg-gray-50 rounded flex items-center justify-center border border-dashed border-gray-300">
                        <div className="text-xs text-gray-500">No data for the selected filters</div>
                      </div>
                    );
                  }

                  const tickFmt = (v: number) => v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v}`;
                  const tooltipFmt = (v: any) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
                  const tooltipStyle = {
                    contentStyle: { fontSize: '10px', padding: '4px 8px', maxWidth: '260px', lineHeight: '1.4' },
                    itemStyle: { fontSize: '10px', padding: '1px 0', margin: 0 },
                    labelStyle: { fontSize: '10px', fontWeight: 600, marginBottom: '2px' },
                    wrapperStyle: { zIndex: 1000 }
                  };

                  return (
                    <div className="h-80 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        {chartType === 'area' ? (
                          <AreaChart data={rechartsData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                            <YAxis tickFormatter={tickFmt} tick={{ fontSize: 10 }} />
                            <Tooltip formatter={tooltipFmt} {...tooltipStyle} />
                            <Legend wrapperStyle={{ fontSize: 10 }} />
                            {reportData.chartData.datasets.map(ds => (
                              <Area
                                key={ds.label}
                                type="monotone"
                                dataKey={ds.label}
                                stackId="1"
                                stroke={ds.borderColor}
                                fill={ds.backgroundColor}
                              />
                            ))}
                          </AreaChart>
                        ) : chartType === 'bar' ? (
                          <BarChart data={rechartsData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                            <YAxis tickFormatter={tickFmt} tick={{ fontSize: 10 }} />
                            <Tooltip formatter={tooltipFmt} {...tooltipStyle} />
                            <Legend wrapperStyle={{ fontSize: 10 }} />
                            {reportData.chartData.datasets.map(ds => (
                              <Bar key={ds.label} dataKey={ds.label} stackId="a" fill={ds.backgroundColor} />
                            ))}
                          </BarChart>
                        ) : (
                          <LineChart data={rechartsData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                            <YAxis tickFormatter={tickFmt} tick={{ fontSize: 10 }} />
                            <Tooltip formatter={tooltipFmt} {...tooltipStyle} />
                            <Legend wrapperStyle={{ fontSize: 10 }} />
                            {reportData.chartData.datasets.map(ds => (
                              <Line
                                key={ds.label}
                                type="monotone"
                                dataKey={ds.label}
                                stroke={ds.borderColor}
                                dot={{ r: 2 }}
                                strokeWidth={2}
                              />
                            ))}
                          </LineChart>
                        )}
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>

              {/* Table Section */}
              <div className="bg-white border border-gray-300 rounded">
                <div className="p-4 border-b border-gray-300 flex items-center justify-between">
                  <h4 className="text-xs font-semibold text-gray-900 flex items-center">
                    <FontAwesomeIcon icon={faTable} className="text-gray-600 mr-2" />
                    Detailed Cost Data
                  </h4>
                  <button
                    onClick={exportToCSV}
                    className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 transition-colors"
                  >
                    <FontAwesomeIcon icon={faDownload} className="mr-1" />
                    Export
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        {reportData.tableData.headers.map((header, index) => (
                          <th key={index} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reportData.tableData.rows.map((row, rowIndex) => (
                        <tr key={rowIndex} className="hover:bg-gray-50">
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex} className="px-4 py-3 text-xs text-gray-900">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white border border-gray-300 rounded p-8 text-center">
              <FontAwesomeIcon icon={faChartBar} className="text-4xl text-gray-400 mb-4" />
              <h4 className="text-xs font-medium text-gray-900 mb-2">Waiting for Cost Data</h4>
              <p className="text-xs text-gray-600 mb-4">
                {!data ? 
                  'Loading cost data from your cloud accounts. Reports will generate automatically once data is available.' :
                  'Configure your filters above. The report will update automatically as you change settings.'
                }
              </p>
              {data && (
                <div className="text-xs text-indigo-600 bg-indigo-50 px-3 py-2 rounded inline-block">
                  💡 Tip: Try changing the Group By dimension or time range to see different insights
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};